import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Extraction from './pages/Extraction';
import Categorization from './pages/Categorization';
import Refinement from './pages/Refinement';
import Comparison from './pages/Comparison';
import Keycloak from 'keycloak-js';
import apiClient, { setAuthToken } from './api/client';
import './App.css';

type KeycloakConfig = {
  url: string;
  realm: string;
  clientId: string;
};

type KeycloakClient = {
  init: (options: { onLoad: 'login-required'; pkceMethod: 'S256'; checkLoginIframe: boolean }) => Promise<boolean>;
  login: () => void;
  logout: (options: { redirectUri: string }) => void;
  updateToken: (minValidity: number) => Promise<boolean>;
  token?: string;
  tokenParsed?: {
    preferred_username?: string;
    email?: string;
  };
};

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Open sidebar by default on desktop
    return window.innerWidth >= 768;
  });
  const [showReopenButton, setShowReopenButton] = useState(false);
  const [authEnabled, setAuthEnabled] = useState<boolean | null>(null);
  const [authReady, setAuthReady] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('anonymous');
  const [keycloakUrl, setKeycloakUrl] = useState<string>('');
  const [keycloakRealm, setKeycloakRealm] = useState<string>('');
  const [keycloakClient, setKeycloakClient] = useState<KeycloakClient | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const refreshTimerRef = useRef<number | null>(null);

  const defaultAppBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const appBaseUrl = process.env.REACT_APP_APP_BASE_URL || defaultAppBaseUrl;

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!sidebarOpen && e.clientX < 50) {
        setShowReopenButton(true);
      } else if (e.clientX > 280) {
        setShowReopenButton(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [sidebarOpen]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const response = await apiClient.get('/auth/status/');
        const { auth_enabled, keycloak_url, keycloak_realm, keycloak_client_id, disable_pkce } = response.data;

        setAuthEnabled(auth_enabled);

        const resolvedUrl = keycloak_url || process.env.REACT_APP_KEYCLOAK_URL || '';
        const resolvedRealm = keycloak_realm || process.env.REACT_APP_KEYCLOAK_REALM || '';
        const resolvedClientId = keycloak_client_id || process.env.REACT_APP_KEYCLOAK_CLIENT_ID || '';

        setKeycloakUrl(resolvedUrl);
        setKeycloakRealm(resolvedRealm);

        if (!auth_enabled) {
          setUsername('anonymous');
          setAuthToken(null);
          setAuthReady(true);
          return;
        }

        if (!resolvedUrl || !resolvedRealm || !resolvedClientId) {
          console.error('Missing Keycloak configuration. Please check environment variables.');
          setAuthEnabled(false);
          setUsername('anonymous');
          setAuthToken(null);
          setAuthReady(true);
          return;
        }

        const keycloakConfig: KeycloakConfig = {
          url: resolvedUrl,
          realm: resolvedRealm,
          clientId: resolvedClientId,
        };

        const keycloak = new Keycloak(keycloakConfig);

        // Determine PKCE method based on DISABLE_PKCE setting
        const pkceMethod = disable_pkce ? undefined : 'S256';

        const authenticated = await keycloak.init({
          onLoad: 'login-required',
          pkceMethod: pkceMethod,
          checkLoginIframe: false,
          redirectUri: appBaseUrl,
        });

        if (!authenticated) {
          keycloak.login({
            redirectUri: appBaseUrl,
            pkceMethod: pkceMethod
          });
          return;
        }

        setKeycloakClient(keycloak);
        setAuthToken(keycloak.token ?? null);
        setUsername(
          keycloak.tokenParsed?.preferred_username ||
          keycloak.tokenParsed?.email ||
          'anonymous'
        );
        setAuthReady(true);

        refreshTimerRef.current = window.setInterval(() => {
          keycloak.updateToken(60).then((refreshed: boolean) => {
            if (refreshed) {
              setAuthToken(keycloak.token ?? null);
              setUsername(
                keycloak.tokenParsed?.preferred_username ||
                keycloak.tokenParsed?.email ||
                'anonymous'
              );
            }
          }).catch(() => keycloak.login());
        }, 30000);
      } catch (error) {
        console.error('Error initializing authentication:', error);
        setAuthEnabled(false);
        setUsername('anonymous');
        setAuthToken(null);
        setAuthReady(true);
      }
    };

    initializeAuth();

    return () => {
      if (refreshTimerRef.current) {
        window.clearInterval(refreshTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Close user menu when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showUserMenu && !target.closest('.user-button-container')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  const handleLogout = () => {
    if (!authEnabled) {
      return;
    }

    if (keycloakClient) {
      keycloakClient.logout({ redirectUri: appBaseUrl || window.location.origin });
      setAuthToken(null);
      setShowUserMenu(false);
    } else if (keycloakUrl && keycloakRealm) {
      const logoutUrl = `${keycloakUrl}/realms/${keycloakRealm}/protocol/openid-connect/logout?redirect_uri=${encodeURIComponent(appBaseUrl || window.location.origin)}`;
      window.location.href = logoutUrl;
    }
  };

  if (authEnabled === null || (authEnabled && !authReady)) {
    return (
      <div className="auth-loading">
        Loading authentication...
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <nav className="navbar">
          <div className="nav-container">
            <Link to="/" className="nav-logo">
              <img src="/orobix-logo.png" alt="Orobix" className="nav-logo-img" />
            </Link>
            <div className="user-button-container">
              <button 
                className="user-button"
                aria-label="User menu"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="user-info">
                  {username}
                </div>
              </button>
              {showUserMenu && (
                <div className="user-menu">
                  <button
                    className={`user-menu-item ${!authEnabled || username === 'anonymous' ? 'disabled' : ''}`}
                    onClick={handleLogout}
                    disabled={!authEnabled || username === 'anonymous'}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>

        <div className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`} onClick={closeSidebar}></div>
        
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h2>Menu</h2>
            <button className="sidebar-close" onClick={closeSidebar} aria-label="Close menu">
              ×
            </button>
          </div>
          <div className="sidebar-menu-toggle-container">
            <button 
              className="sidebar-menu-toggle" 
              onClick={toggleSidebar} 
              aria-label={sidebarOpen ? 'Collapse' : 'Expand'}
            >
              {sidebarOpen ? '<<' : '>>'}
            </button>
          </div>
          <nav className="sidebar-nav">
            <SidebarLink to="/">Home</SidebarLink>
            <SidebarLink to="/extraction">Extraction</SidebarLink>
            <SidebarLink to="/categorization">Categorization</SidebarLink>
            <SidebarLink to="/refinement">Refinement</SidebarLink>
            <SidebarLink to="/comparison">Comparison</SidebarLink>
          </nav>
          <div className="sidebar-footer">
            <p className="sidebar-footer-text">Copyright ©2025 Orobix s.r.l.</p>
          </div>
        </aside>

        {!sidebarOpen && showReopenButton && (
          <button 
            className="sidebar-reopen-button" 
            onClick={toggleSidebar}
            aria-label="Expand"
            onMouseLeave={() => setShowReopenButton(false)}
          >
            &gt;&gt;
          </button>
        )}

        <main className={`main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/extraction" element={<Extraction />} />
            <Route path="/categorization" element={<Categorization />} />
            <Route path="/refinement" element={<Refinement />} />
            <Route path="/comparison" element={<Comparison />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

function SidebarLink({ to, children }: { to: string; children: React.ReactNode }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`sidebar-link ${isActive ? 'active' : ''}`}
    >
      {children}
    </Link>
  );
}

export default App;

